import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { DamageAnimation } from './DamageAnimation';
import { useDamageAnimationContext } from './DamageAnimationContext';

// Slot coordinates (cell-based) - same as StrategemTargetAnimation
const slotCoordinates = {
  // Opponent Front (slots 0-4)
  'opponent_front_0': { cellX: 4, cellY: 10 },
  'opponent_front_1': { cellX: 14, cellY: 10 },
  'opponent_front_2': { cellX: 24, cellY: 9 },
  'opponent_front_3': { cellX: 32, cellY: 10 },
  'opponent_front_4': { cellX: 42, cellY: 9 },
  
  // Opponent Flank (slots 0-4)
  'opponent_flank_0': { cellX: 52, cellY: 10 },
  'opponent_flank_1': { cellX: 52, cellY: 11 },
  'opponent_flank_2': { cellX: 70, cellY: 10 },
  'opponent_flank_3': { cellX: 79, cellY: 10 },
  'opponent_flank_4': { cellX: 86, cellY: 10 },
  
  // Your Front (slots 0-4)
  'your_front_0': { cellX: 3, cellY: 18 },
  'your_front_1': { cellX: 15, cellY: 18 },
  'your_front_2': { cellX: 22, cellY: 18 },
  'your_front_3': { cellX: 32, cellY: 17 },
  'your_front_4': { cellX: 39, cellY: 18 },
  
  // Your Flank (slots 0-4)
  'your_flank_0': { cellX: 52, cellY: 18 },
  'your_flank_1': { cellX: 62, cellY: 18 },
  'your_flank_2': { cellX: 69, cellY: 17 },
  'your_flank_3': { cellX: 81, cellY: 18 },
  'your_flank_4': { cellX: 89, cellY: 17 },
};

const GRID_CELL_SIZE = 20;

const getSlotPixelPosition = (zoneName, index) => {
  const slotKey = `${zoneName}_${index}`;
  const coords = slotCoordinates[slotKey];
  
  if (!coords) {
    console.warn(`SlotDeathAnimations: No coordinates for ${slotKey}`);
    return null;
  }
  
  return {
    x: (coords.cellX * GRID_CELL_SIZE) + (GRID_CELL_SIZE / 2),
    y: (coords.cellY * GRID_CELL_SIZE) + (GRID_CELL_SIZE / 2)
  };
};

/**
 * Component to render death animations in an empty slot
 * Uses portal to render at fixed screen positions based on slot coordinates
 * @param {string} zoneName - Zone name (e.g., 'opponent_front')
 * @param {number} index - Slot index
 */
export const SlotDeathAnimations = ({ zoneName, index }) => {
  const { getSlotAnimations } = useDamageAnimationContext();
  const slotAnims = getSlotAnimations(zoneName, index);
  
  const position = useMemo(() => getSlotPixelPosition(zoneName, index), [zoneName, index]);

  if (!slotAnims || slotAnims.length === 0 || !position) {
    return null;
  }

  // Use portal to render at fixed screen position
  return createPortal(
    <div
      key={`${zoneName}_slot_anim_${index}`}
      style={{
        position: 'fixed',
        left: `${position.x}px`,
        top: `${position.y}px`,
        transform: 'translate(-50%, -50%)',
        width: '100px',
        height: '100px',
        backgroundColor: 'transparent',
        overflow: 'visible',
        zIndex: 10000,
        pointerEvents: 'none',
      }}
    >
      {slotAnims.map((anim) => (
        <DamageAnimation
          key={anim.key}
          x={50} // Center of container
          y={50} // Center of container
          onComplete={anim.onComplete}
          animationKey={anim.key}
        />
      ))}
    </div>,
    document.body
  );
};

